import * as mobx from 'mobx';
import {
    FormFieldProps,
    FormViewModelController,
    FormViewModelItemState,
    FormVmItemConfigType,
    FormVmValueType,
    FormVmValueValidationStatus,
    FormVmValueValidatorResultType,
    InitialData,
} from '../form-viewmodel-controller';

type FormVmConfig = Omit<FormVmItemConfigType, 'name'>;

abstract class AbstractFormViewModelController implements FormViewModelController {
    public static readonly configDefaults: FormVmConfig = {
        defaultValue: '', touched: false, validators: [], formatters: []
    };

    public static readonly defaultValidationStatus: FormVmValueValidatorResultType = {
        valid: true, validationMessage: null
    };

    /**
     * Check provided configuration and throws an exception in case of invalid.
     */
    private static checkProvidedConfiguration = (configList: FormVmItemConfigType[], name: string): void => {
        const nonEmptyArray = Array.isArray(configList) && configList.length;
        const nameProvided = configList?.every(item => typeof item.name === 'string' && item.name.length);

        if (nonEmptyArray && nameProvided) return;

        throw new TypeError(`${name.toUpperCase()}: Invalid configuration provided`);
    };

    @mobx.observable
    private readonly _config: Record<string, FormVmConfig> = {};
    private readonly _keys: string[] = [];
    private readonly _initialValues: Record<string, FormVmValueType> = {};
    @mobx.observable
    private _values: InitialData = {};

    /**
     * Configure vm items with provided validation, formatting, defaults opts.
     */
    private configure = (configList: FormVmItemConfigType[]): void => {
        for (const { name, ...rest } of configList) {
            this._config[name] = { ...AbstractFormViewModelController.configDefaults, ...rest };
            this._keys.push(name);
        }
    };

    /**
     * Uses configuration to initialize vm values by provided data.
     * Initial values are stored for backup compatibility.
     */
    @mobx.action
    private initialize = (initial: InitialData): void => {
        this._keys.forEach((key: string) => {
            this._values[key] = Reflect.has(initial, key) ? initial[key] : this._config[key].defaultValue;
        });

        Object.assign(this._initialValues, this._values);
    };

    /**
     * Perform configured validation and return results.
     */
    @mobx.action
    private validate = (key: string): FormVmValueValidatorResultType[] =>
        this._config[key].validators
            .map(_validate => ({ validator: _validate, ..._validate(this.getValue(key), this._values) }))
    ;

    /**
     * Transform value with configured formatters.
     */
    @mobx.action
    private applyFormatters = (key: string, value: FormVmValueType): FormVmValueType =>
        this._config[key].formatters
            .reduce((_value, formatter) => formatter(_value), value)
    ;

    /**
     * Mark the value as unchanged from it's initial state. (Ex. cancelling or resetting form data on UI).
     */
    @mobx.action
    private setValuePristine = (key: string): void => {
        this._config[key].touched = false;
    };

    protected constructor(initialValues: InitialData = {}, name = 'FormViewModel', configList: FormVmItemConfigType[]) {
        AbstractFormViewModelController.checkProvidedConfiguration(configList, name);
        this.configure(configList);
        this.initialize(initialValues);
    }

    @mobx.action
    public setValueTouched = (key: string): void => {
        this._config[key].touched = true;
    };

    @mobx.action
    public setValue = (key, val): void => {
        this._values[key] = this.applyFormatters(key, val);
    };

    public getValue = (key: string): FormVmValueType => this._values[key];

    /**
     * Returns the first invalid result of validation or default valid state.
     */
    public getValueValidationState = (key: string): FormVmValueValidationStatus => (
        {
            ...(this.validate(key).find(state => !state.valid) || AbstractFormViewModelController.defaultValidationStatus),
            touched: this._config[key].touched,
            changed: this._values[key] !== this._initialValues[key]
        }
    );

    /**
     * Returns composed props for value by key
     */
    public getFormVmValuePropsByKey(key: string): FormFieldProps {
        return {
            value: this.getValue(key),
            setValue: value => this.setValue(key, value),
            setTouched: () => this.setValueTouched(key),
            ...this.getValueValidationState(key)
        };
    }

    /**
     * Returns true if current value differs from the initial one.
     */
    public isValueChanged = (key: string): boolean => this.getValue(key) !== this._initialValues[key];

    public isValueValid = (key: string): boolean => this.getValueValidationState(key).valid;

    public isValueTouched = (key: string): boolean => this._config[key].touched;

    public isVmValid = (): boolean => this._keys.every(this.isValueValid);

    protected isVmValidExcept = (...excludedKeys: string[]): boolean => {
        if (excludedKeys.length === 0) {
            return this._keys.every(this.isValueValid);
        }
        const excluded = new Set(excludedKeys);
        return this._keys.every(key => excluded.has(key) || this.isValueValid(key));
    };

    public isVmPristine = (): boolean => !this._keys.map(this.isValueTouched).every(Boolean);

    public isVmChanged = (): boolean => this._keys.some(this.isValueChanged);

    /**
     * Mark values as touched.
     */
    @mobx.action
    public setVmTouched = (): void => this._keys.forEach(this.setValueTouched);

    /**
     * Reset values and config to initial state.
     */
    @mobx.action
    public resetVm = (): void => {
        Object.assign(this._values, this._initialValues);
        this._keys.forEach(this.setValuePristine);
    };

    /**
     * Return vm values info.
     */
    public getVmState = (): FormViewModelItemState[] => this._keys
        .map(key => ({
            name: key,
            value: this.getValue(key),
            valid: this.isValueValid(key),
            touched: this.isValueTouched(key),
            validationState: this.validate(key)
        }))
    ;
}

export {
    AbstractFormViewModelController
};
